import { useCallback, useMemo } from "react"
import { ReactFlow, ReactFlowProvider } from "@xyflow/react"
import type { Node, Edge } from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import { ArrowRight, Calendar } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { LeadAgentNode } from "@/components/home/multi-agent/nodes/lead-agent-node"
import { SpecializedAgentNode } from "@/components/home/multi-agent/nodes/specialized-agent-node"
import { DemoEdge } from "@/components/home/multi-agent/edges/demo-edge"

/**
 * Custom node types for the hero workflow diagram.
 */
const nodeTypes = {
  leadAgent: LeadAgentNode,
  specializedAgent: SpecializedAgentNode,
}

/**
 * Custom edge types for the hero workflow diagram.
 */
const edgeTypes = {
  demoEdge: DemoEdge,
}

/**
 * Static nodes for the multi-agent hero demonstration.
 * Supervisor at left, 6 specialized agents arranged on the right.
 */
const demoNodes: Node[] = [
  {
    id: "supervisor",
    type: "leadAgent",
    position: { x: 50, y: 220 },
    data: {
      name: "Supervisor",
      agentCount: 6,
    },
  },
  {
    id: "sales",
    type: "specializedAgent",
    position: { x: 380, y: 0 },
    data: {
      name: "Sales Assistant",
      initials: "SA",
      description: "Lead generation and sales outreach",
      letter: "A",
      color: "primary",
    },
  },
  {
    id: "social",
    type: "specializedAgent",
    position: { x: 680, y: 0 },
    data: {
      name: "Social Pulse",
      initials: "SP",
      description: "Social media management",
      letter: "B",
      color: "secondary",
    },
  },
  {
    id: "expense",
    type: "specializedAgent",
    position: { x: 380, y: 150 },
    data: {
      name: "Expense Bot",
      initials: "EB",
      description: "Finance and expense tracking",
      letter: "C",
      color: "primary",
    },
  },
  {
    id: "support",
    type: "specializedAgent",
    position: { x: 680, y: 150 },
    data: {
      name: "Support Agent",
      initials: "SU",
      description: "Customer support automation",
      letter: "D",
      color: "secondary",
    },
  },
  {
    id: "content",
    type: "specializedAgent",
    position: { x: 380, y: 300 },
    data: {
      name: "Content Writer",
      initials: "CW",
      description: "Blog and content creation",
      letter: "E",
      color: "primary",
    },
  },
  {
    id: "research",
    type: "specializedAgent",
    position: { x: 680, y: 300 },
    data: {
      name: "Research Bot",
      initials: "RB",
      description: "Market research and analysis",
      letter: "F",
      color: "secondary",
    },
  },
]

/**
 * Static edges showing agent communication network.
 * Supervisor connects to all agents, and agents communicate with each other.
 */
const demoEdges: Edge[] = [
  // Supervisor to all agents
  { id: "e-sup-sales", source: "supervisor", target: "sales", type: "demoEdge" },
  { id: "e-sup-social", source: "supervisor", target: "social", type: "demoEdge" },
  { id: "e-sup-expense", source: "supervisor", target: "expense", type: "demoEdge" },
  { id: "e-sup-support", source: "supervisor", target: "support", type: "demoEdge" },
  { id: "e-sup-content", source: "supervisor", target: "content", type: "demoEdge" },
  { id: "e-sup-research", source: "supervisor", target: "research", type: "demoEdge" },
  // Agent to agent communication
  { id: "e-sales-social", source: "sales", target: "social", type: "demoEdge" },
  { id: "e-sales-expense", source: "sales", target: "expense", type: "demoEdge" },
  { id: "e-social-support", source: "social", target: "support", type: "demoEdge" },
  { id: "e-expense-content", source: "expense", target: "content", type: "demoEdge" },
  { id: "e-support-research", source: "support", target: "research", type: "demoEdge" },
  { id: "e-content-research", source: "content", target: "research", type: "demoEdge" },
]

/**
 * Inner component with React Flow visualization for hero section.
 */
function MultiAgentDiagram() {
  const nodes = useMemo(() => demoNodes, [])
  const edges = useMemo(() => demoEdges, [])

  const onInit = useCallback(() => {
    // Diagram is read-only, no initialization needed
  }, [])

  return (
    <div className="h-[500px] w-full overflow-hidden rounded-2xl bg-neutral-800">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={onInit}
        defaultViewport={{ x: 0, y: 0, zoom: 0.85 }}
        minZoom={0.5}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        className="!bg-neutral-800"
        fitView
        fitViewOptions={{ padding: 0.2 }}
      />
    </div>
  )
}

/**
 * Hero section for the Multi-Agent Orchestration product page.
 * Features a dark gradient background with the React Flow diagram.
 */
export function HeroSection() {
  const navigate = useNavigate()

  return (
    <section className="relative overflow-hidden bg-neutral-900 pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Text Content - Centered */}
        <div className="mb-12 text-center">
          <Badge className="mb-6 border-primary/30 bg-primary/10 text-primary">
            Multi-Agent Orchestration
          </Badge>
          <h1 className="mb-6 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            One Command Center,{" "}
            <span className="text-primary">Unlimited AI Agents</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-neutral-400">
            Build AI teams that work together. Your Lead Agent intelligently routes
            tasks to specialized agents, enabling seamless collaboration at scale.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="group bg-primary text-white hover:bg-primary/90"
              onClick={() => navigate("/signup")}
            >
              Start Building
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-neutral-700 bg-transparent text-white hover:bg-neutral-800"
              onClick={() => navigate("/demo")}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Book a Demo
            </Button>
          </div>
        </div>

        {/* React Flow Diagram */}
        <div className="relative">
          <ReactFlowProvider>
            <MultiAgentDiagram />
          </ReactFlowProvider>
          {/* Decorative glow */}
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-r from-primary/20 via-transparent to-secondary/20 opacity-50 blur-2xl" />
        </div>
      </div>
    </section>
  )
}
